import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const LINES = ["Handmade crochet,", "one stitch", "at a time."];
const EASE = [0.22, 1, 0.36, 1];

const HERO_PHOTOS = [
  { src: "/photos/hats.jpg", alt: "Pink striped hat with a crocheted flower" },
  { src: "/photos/owl-hat.jpg", alt: "Child's crocheted owl hat" },
  { src: "/photos/extra-cactus.jpg", alt: "Cactus hat with red hearts" },
  { src: "/photos/extra-earwarmers.jpg", alt: "Set of four crocheted ear warmers" },
  { src: "/photos/extra-reindeer.jpg", alt: "Reindeer ear warmer with flowers" },
  { src: "/photos/amigurumi.jpg", alt: "Trio of crocheted bunny dolls" },
  { src: "/photos/stuffies.jpg", alt: "Crocheted penguin stuffie" },
];

export default function Hero() {
  const ref = useRef(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhotoIdx((i) => (i + 1) % HERO_PHOTOS.length), 6000);
    return () => clearInterval(t);
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="bg-blend-parch relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28">
      <span aria-hidden="true" className="stroke-word pointer-events-none absolute -bottom-8 left-0 select-none font-serif text-[22vw] font-bold leading-none tracking-tight">
        crochet
      </span>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8 lg:px-16">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-badge-region"
            className="mb-8 inline-flex items-center gap-2 rounded-full bg-[#EFE6D8] px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#5C4D42]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#8B9A82]" />
            Guelph–Waterloo · Ships across Canada &amp; US
          </motion.p>

          <h1 className="font-serif text-5xl font-semibold leading-[1.04] tracking-tight text-[#2C2520] sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic text-[#A35C45]" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-md text-base leading-relaxed text-[#6E6259] sm:text-lg"
          >
            Hats, blankets, booties, cardigans &amp; cuddly stuffies — each piece made to order by hand,
            with the same love as that very first stitch.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#order"
              data-testid="hero-cta-order-now"
              className="group rounded-full bg-[#2C2520] px-7 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#A35C45]"
            >
              Start a custom order
              <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#collection"
              data-testid="hero-cta-browse-collection"
              className="rounded-full border border-[#2C2520]/25 px-7 py-3.5 text-sm font-medium text-[#2C2520] transition-colors duration-300 hover:border-[#A35C45] hover:text-[#A35C45]"
            >
              Browse the collection
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="relative"
        >
          <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[2rem] rounded-tr-[7rem] border border-[#E6DFD5] shadow-[0_30px_60px_-20px_rgba(44,37,32,0.25)]">
            <div className="relative aspect-[4/5] w-full" data-testid="hero-image-yarn">
              {HERO_PHOTOS.map((p, i) => (
                <motion.img
                  key={p.src}
                  src={p.src}
                  alt={p.alt}
                  initial={false}
                  animate={{ opacity: i === photoIdx ? 1 : 0, scale: i === photoIdx ? 1 : 1.05 }}
                  transition={{ duration: 1.4, ease: "easeInOut" }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ))}
            </div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2C2520]/25 to-transparent" />
            <div className="absolute bottom-4 right-5 flex gap-1.5">
              {HERO_PHOTOS.map((p, i) => (
                <button
                  key={p.src}
                  onClick={() => setPhotoIdx(i)}
                  aria-label={`Show photo ${i + 1}`}
                  data-testid={`hero-photo-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${i === photoIdx ? "w-5 bg-[#FDFBF7]" : "w-1.5 bg-[#FDFBF7]/50 hover:bg-[#FDFBF7]/80"}`}
                />
              ))}
            </div>
          </motion.div>
          <motion.div
            style={{ y: badgeY }}
            data-testid="hero-badge-handmade"
            className="absolute -left-5 bottom-10 rounded-2xl border border-[#E6DFD5] bg-[#FDFBF7]/95 px-5 py-4 shadow-lg backdrop-blur"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B]">Est. from Mom&apos;s hook</p>
            <p className="mt-1 font-serif text-lg font-semibold italic text-[#2C2520]">Every piece made by one pair of hands</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
